import useAppMain from "src/hooks/useAppMain";
import {
  CLIENT_ROUTES,
  CLIENT_TAB_LIST,
  PATH_NAMES,
} from "src/utils/constants";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  Text,
  Input,
  List,
  ScrollArea,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { Fragment, useCallback, useEffect, useState, useContext } from "react";
import { BiSearch } from "react-icons/bi";
import { checkIsWeb, debounce } from "src/utils/utils";
import {
  GetLocationServicesNameGroupQuery,
  GetMeetingPageQuery,
  useGetLocationServicesNameGroupQuery,
  useGetMeetingPageLazyQuery,
} from "src/generated/graphql";
import { getDateRanges } from "src/utils/dateHelper";
import { AiOutlineCalendar } from "react-icons/ai";
import { useModals } from "@mantine/modals";
import DateRange from "src/modules/Dashboard/DateRange";
import { useScheduleEventContext } from "src/api/context/ScheduleEventContext";
import ListItem from "./ListItem";
import { AppContext } from "src/api/AppContext";

const bgGrey = "#F6F6F6";
const btnCss = {
  root: {
    //background: bgGrey,
    border: 0,
    height: 25,
    fontSize: 12,
    paddingLeft: 10,
    paddingRight: 10,
    minWidth: 50,
  },
};

export type IMeetingPage = GetMeetingPageQuery["meetingPage"]["items"];

function EventsList() {
  const theme = useMantineTheme();
  useAppMain(CLIENT_TAB_LIST.SCEDULE_EVENT, true);
  const navigate = useNavigate();
  const [searchedList, setSearchedList] = useState<IMeetingPage>([]);
  const {
    filters,
    setFilters,
    setSearchedValue,
    searchedValue,
    services,
    handleService,
  } = useScheduleEventContext();
  const modals = useModals();
  const isWeb = checkIsWeb();
  const { currentClient } = useContext(AppContext);
  const [servicesData, setservicesData] = useState<
    GetLocationServicesNameGroupQuery["locationServiceMany"]
  >([]);

  useGetLocationServicesNameGroupQuery({
    variables: { locationId: filters.locations[0] },
    onCompleted: (data) => {
      setservicesData(
        data.locationServiceMany.filter((e) =>
          filters.isIndivdual
            ? e.organizationService.isIndivdual
            : !e.organizationService.isIndivdual
        )
      );
    },
  });

  const [getMeetings, { data: meetings, loading }] = useGetMeetingPageLazyQuery(
    { fetchPolicy: "cache-and-network" }
  );

  useEffect(() => {
    if (!filters.locations.length) {
      navigate(-2);
    }
  }, []);

  useEffect(() => {
    setSearchedList(meetings?.meetingPage.items || []);
  }, [meetings?.meetingPage.items]);

  const fetchMeeting = (name?: string) => {
    filters.dateRange &&
      getMeetings({
        variables: {
          pagination: {
            filters: [
              {
                field: "name",
                type: "text",
                value: name ?? searchedValue,
              },
              {
                field: "locationId",
                type: "text",
                value: filters.locations[0],
              },
              { type: "select", field: "serviceId", values: services },
              {
                type: "select",
                field: "isIndivdual",
                parentRelationField: "service.organizationService",
                values: [filters.isIndivdual ? "true" : "false"],
              },
              {
                type: "text",
                field: "ignoreAttendeeId",
                value: currentClient?.attendee?.id,
              },
            ],
            count: 10,
          },
          startAt: filters.dateRange[0],
          endAt: filters.dateRange[1],
        },
      });
  };

  useEffect(() => {
    fetchMeeting();
  }, [filters, services]);

  const handleNav = (id: string) => {
    navigate(`/${CLIENT_ROUTES.MY_CALENDAR}/${PATH_NAMES.ENROLL}/${id}`);
  };

  const sendRequestSearch = (value: string) => {
    fetchMeeting(value);
  };

  const debouncedSendRequest = useCallback(
    debounce((value) => {
      sendRequestSearch(value);
    }, 800),
    [filters]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchedValue(value);
    debouncedSendRequest(value);
  };

  const ButtonDate = useCallback(
    (text: String, dateRange: [Date, Date]) => {
      const isSelected =
        filters.dateRange?.length == 2 &&
        dateRange[0].valueOf() == filters.dateRange[0].valueOf() &&
        dateRange[1].valueOf() == filters.dateRange[1].valueOf();
      return (
        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={btnCss}
          onClick={() => {
            setFilters({
              ...filters,
              dateRange,
            });
          }}
          sx={{ background: isSelected ? "#DDDDDD" : bgGrey }}
          style={{
            backgroundColor: theme.colors.primary[isSelected ? 0 : 6],
            color: theme.colors.primaryTextColor[isSelected ? 0 : 6],
          }}
        >
          {text}
        </Button>
      );
    },
    [filters]
  );

  const ServiceBtn = useCallback(
    (id, name) => {
      const isSelected = services.includes(id);
      return (
        <Button
          radius="xl"
          variant="outline"
          size="sm"
          styles={btnCss}
          onClick={() => handleService(id)}
          style={{
            backgroundColor: theme.colors.primary[isSelected ? 0 : 6],
            color: theme.colors.primaryTextColor[isSelected ? 0 : 6],
          }}
        >
          {name}
        </Button>
      );
    },
    [services]
  );

  const getDateRangeText = () => {
    if (filters.dateRange) {
      if (filters.dateRange[0].getDate() == filters.dateRange[1].getDate()) {
        return filters.dateRange[0].toLocaleDateString();
      } else {
        return (
          filters.dateRange[0].toLocaleDateString() +
          " - " +
          filters.dateRange[1].toLocaleDateString()
        );
      }
    }
    return "Date Range";
  };

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Date Range",
      children: (
        <DateRange
          filters={filters}
          setFilters={setFilters}
          minDate={new Date()}
        />
      ),
    });
  }, [filters]);

  // if (loading)
  //   return <Loader width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />;
  return (
    <>
      <ScrollArea
        style={{ width: "100%" }}
        scrollbarSize={6}
        type={isWeb ? "hover" : "never"}
      >
        <Flex justify={"space-between"} gap={5}>
          {/* ADDED PLUS 1 GIVING US Correct DATE meetings */}
          {ButtonDate("Today", [
            new Date(new Date().setHours(0, 0, 0, 0)),
            new Date(new Date().setHours(23, 59, 59, 999) + 1),
          ])}

          {ButtonDate("Tomorrow", [
            new Date(new Date().setHours(24, 0, 0, 0)),
            new Date(new Date().setHours(47, 59, 59, 999) + 1),
          ])}

          {ButtonDate("This Week", getDateRanges().thisWeek as [Date, Date])}

          <Button
            radius="xl"
            variant="outline"
            size="sm"
            styles={btnCss}
            onClick={handleModal}
            rightIcon={<AiOutlineCalendar size="1rem" />}
            style={{
              backgroundColor: theme.colors.primary[6],
              color: theme.colors.primaryTextColor[6],
            }}
          >
            {getDateRangeText()}
          </Button>
        </Flex>
      </ScrollArea>

      <Input
        mt={"sm"}
        icon={<BiSearch color="black" />}
        placeholder="Search Class"
        value={searchedValue}
        onChange={handleSearchChange}
        sx={{
          borderColor: "#EBEBEB",
          input: {
            background: bgGrey,
          },
        }}
      />

      {!!servicesData?.length && (
        <ScrollArea
          style={{ width: "100%" }}
          scrollbarSize={6}
          type={isWeb ? "hover" : "never"}
          m={"15px 0"}
        >
          <Flex gap={"xs"} pb={8}>
            {servicesData?.map((s) => (
              <Fragment key={s.id}>
                {ServiceBtn(s.id, s.organizationService.name)}
              </Fragment>
            ))}
          </Flex>
        </ScrollArea>
      )}

      <List
        my={10}
        styles={{
          itemWrapper: {
            width: "100%",
          },
          item: {
            listStyle: "none",
            cursor: "pointer",
            borderBottom: `1px solid #DDDDDD`,
            color: theme.colors.secondaryTextColor[6],
          },
        }}
      >
        {searchedList?.map((meet) => {
          return (
            <ListItem key={meet.id} handleNav={handleNav} meeting={meet} />
          );
        })}
      </List>

      {loading ? (
        <Loader width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />
      ) : (
        !searchedList?.length && (
          <Text
            align="center"
            fw={700}
            size={"xl"}
            mt={"xl"}
            color={theme.colors.secondaryTextColor[6]}
          >
            No Class found
          </Text>
        )
      )}
    </>
  );
}

export default EventsList;
